import React, { useId } from "react";
import Styles from "../styles/Analytics.module.css";
import { formatNumber } from "../utils/analyticsUtils";

const VIEW_WIDTH = 640;
const VIEW_HEIGHT = 240;
const PADDING = { top: 16, right: 12, bottom: 36, left: 48 };

const CHART_WIDTH = VIEW_WIDTH - PADDING.left - PADDING.right;
const CHART_HEIGHT = VIEW_HEIGHT - PADDING.top - PADDING.bottom;

// Round the chart max up to a friendly 1/2/5 * 10^n value so the axis
// gridlines are readable.
function niceCeil(value) {
  if (value <= 5) return Math.max(1, Math.ceil(value));
  const power = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / power;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * power;
}

function GridLines({ max, valueFormatter }) {
  const steps = [0, 0.25, 0.5, 0.75, 1];
  return steps.map((step) => {
    const y = PADDING.top + CHART_HEIGHT * (1 - step);
    return (
      <g key={step}>
        <line
          x1={PADDING.left}
          x2={VIEW_WIDTH - PADDING.right}
          y1={y}
          y2={y}
          className={Styles.gridLine}
        />
        <text
          x={PADDING.left - 10}
          y={y + 4}
          textAnchor="end"
          className={Styles.axisLabel}
        >
          {valueFormatter(max * step)}
        </text>
      </g>
    );
  });
}

function XAxisLabels({ data }) {
  const step = CHART_WIDTH / Math.max(data.length, 1);
  const labelEvery = Math.ceil(data.length / 8);
  return data.map((bucket, index) => {
    const show = index % labelEvery === 0 || index === data.length - 1;
    if (!show) return null;
    return (
      <text
        key={`${bucket.label}-${index}`}
        x={PADDING.left + step * index + step / 2}
        y={VIEW_HEIGHT - 12}
        textAnchor="middle"
        className={Styles.axisLabel}
      >
        {bucket.label}
      </text>
    );
  });
}

export function BarChart({
  data = [],
  color = "#f97316",
  valueFormatter = formatNumber,
  ariaLabel = "Bar chart",
}) {
  const max = niceCeil(Math.max(...data.map((bucket) => bucket.value), 0));
  const step = CHART_WIDTH / Math.max(data.length, 1);
  const barWidth = Math.min(step * 0.6, 44);

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      className={Styles.chartSvg}
      role="img"
      aria-label={ariaLabel}
    >
      <title>{ariaLabel}</title>
      <GridLines max={max} valueFormatter={valueFormatter} />

      {data.map((bucket, index) => {
        const barHeight =
          bucket.value > 0
            ? Math.max((bucket.value / max) * CHART_HEIGHT, 4)
            : 2;
        const x = PADDING.left + step * index + (step - barWidth) / 2;
        const y = PADDING.top + CHART_HEIGHT - barHeight;

        return (
          <rect
            key={`${bucket.label}-${index}`}
            tabIndex={0}
            aria-label={`${bucket.fullLabel || bucket.label}: ${valueFormatter(
              bucket.value,
            )}`}
            x={x}
            y={y}
            width={barWidth}
            height={barHeight}
            rx={4}
            fill={color}
            opacity={bucket.value > 0 ? 0.92 : 0.18}
            className={Styles.bar}
          >
            <title>{`${bucket.fullLabel || bucket.label}: ${valueFormatter(
              bucket.value,
            )}`}</title>
          </rect>
        );
      })}

      <XAxisLabels data={data} />
    </svg>
  );
}

export function LineChart({
  data = [],
  color = "#3b82f6",
  valueFormatter = formatNumber,
  ariaLabel = "Line chart",
}) {
  const gradientId = `line-gradient-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const max = niceCeil(Math.max(...data.map((bucket) => bucket.value), 0));

  const points = data.map((bucket, index) => ({
    x:
      data.length === 1
        ? PADDING.left + CHART_WIDTH / 2
        : PADDING.left + (CHART_WIDTH * index) / (data.length - 1),
    y: PADDING.top + CHART_HEIGHT - (bucket.value / max) * CHART_HEIGHT,
    bucket,
  }));

  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`)
    .join(" ");

  const baseline = PADDING.top + CHART_HEIGHT;
  const areaPath =
    points.length > 0
      ? `${linePath} L${points[points.length - 1].x},${baseline} L${
          points[0].x
        },${baseline} Z`
      : "";

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      className={Styles.chartSvg}
      role="img"
      aria-label={ariaLabel}
    >
      <title>{ariaLabel}</title>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      <GridLines max={max} valueFormatter={valueFormatter} />

      {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
      {linePath && (
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {points.map((point, index) => (
        <circle
          key={`${point.bucket.label}-${index}`}
          tabIndex={0}
          aria-label={`${point.bucket.fullLabel || point.bucket.label}: ${valueFormatter(
            point.bucket.value,
          )}`}
          cx={point.x}
          cy={point.y}
          r={point.bucket.value > 0 ? 4 : 2.5}
          fill={color}
          className={Styles.dot}
        >
          <title>{`${point.bucket.fullLabel || point.bucket.label}: ${valueFormatter(
            point.bucket.value,
          )}`}</title>
        </circle>
      ))}

      <XAxisLabels data={data} />
    </svg>
  );
}
